import { useContext, useState, MouseEvent } from 'react';
import { Store } from '../store/Store';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import jwt from 'jwt-decode';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import { PAGE_URLS } from '../routes/routes';

import B2CClientConnector from '../interfaces/B2CClientConnector';
import {
  getLatestTimestamp,
  isRefreshing,
  hasWarning,
  allowSynchronisation,
  getStatusTextFromLog,
  truncateText,
  isTruncated,
} from '../utils/clientConnectorUtils';
// import StatusIcon from '../StatusIcon';
// Update the path below to the correct location of StatusIcon, for example:
import StatusIcon from './StatusIcon';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(timezone);

const REFRESH_DETAIL_MAX_LENGTH = 50;

const Business2CloudConnectorManageCard = ({
  clientConnector,
  onProcessClientConnectorClick,
  onTrialBalanceClick,
}: {
  clientConnector: B2CClientConnector;
  onProcessClientConnectorClick: (
    event: MouseEvent<HTMLButtonElement>,
    clientConnector: B2CClientConnector,
    fullTransactionSync: boolean
  ) => Promise<void>;
  onTrialBalanceClick: (
    event: MouseEvent<HTMLButtonElement>,
    clientConnector: B2CClientConnector
  ) => Promise<void>;
}) => {
  const [searchParams] = useSearchParams();
  const { connectorType } = useParams();
  const storeContext = useContext(Store);
  const currentRole = jwt<any>(storeContext?.store?.jwt)?.Role?.toLowerCase() ?? '';
  const search = searchParams.get('search') as string;

  const [showModal, setShowModal] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  const latestTimestamp = getLatestTimestamp(clientConnector.Log);
  const refreshing = isRefreshing(clientConnector.Log);
  const warning = hasWarning(clientConnector.Log);
  const canSync = allowSynchronisation(clientConnector.Log);
  const rawDetail = clientConnector.Log?.RefreshDetail || 'No detail';
  const truncatedDetail = truncateText(rawDetail, REFRESH_DETAIL_MAX_LENGTH);
  const truncated = isTruncated(rawDetail, REFRESH_DETAIL_MAX_LENGTH);

  const filterMatches =
    !search || clientConnector.Name.toLowerCase().includes(search.toLowerCase());

  if (!filterMatches) return <></>;

  const onCopyRefreshDetailToClipboard = () => {
    if (clientConnector.Log?.RefreshDetail) {
      navigator.clipboard.writeText(clientConnector.Log.RefreshDetail);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 3000);
    }
  };

  const renderStatusText = () => {
    if (['DISCONNECTED', 'DELETED'].includes(clientConnector.Status ?? '')) return <></>;
    if (!latestTimestamp?.value) {
      return <small className="light-text">Never started</small>;
    }
    return (
      <small className="dark-text">
        {getStatusTextFromLog(clientConnector.Log)}{' '}
        <b>{dayjs(latestTimestamp.value).fromNow()}</b>
      </small>
    );
  };

  const renderProgressAnimation = () => {
    if (['DISCONNECTED', 'DELETED'].includes(clientConnector.Status ?? '')) return null;
    if (refreshing) {
      return (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <img src="/img/sync.svg" alt="Sync" className="sync-icon" />
        </div>
      );
    }
    return null;
  };

  const REFRESH_STATUSES_TO_HIDE = ['CONNECTED', 'ACTIVE'];
  const shouldHideRefreshDetail = REFRESH_STATUSES_TO_HIDE.includes(
    (clientConnector.Status ?? '').toUpperCase()
  );

  const renderBody = () => {
    if (clientConnector.Status === 'DELETED') return '';

    if (clientConnector.Status === 'DISCONNECTED') {
      return (
        <small>
          {currentRole.includes('admin') && (
            <span>
              <b>To reconnect, please go to the </b>
              <b>
                <Link
                  to={PAGE_URLS.BUSINESS_2_CLOUD_CONNECTOR_MANAGE.replace(
                    ':connectorType',
                    connectorType as string
                  )}
                  style={{ fontSize: '15px' }}
                >
                  Manage
                </Link>
              </b>
              <b> tab.</b>
            </span>
          )}
        </small>
      );
    }

    if (clientConnector.Log?.Forbidden) {
      return (
        <small className="d-flex flex-column gap-1 text-warning">
          <span>
            <b>This connector may have been disconnected.</b>
          </span>
          {currentRole.includes('admin') && (
            <span>
              You may need to re-authorise or disconnect fully from the{' '}
              <Link
                to={PAGE_URLS.BUSINESS_2_CLOUD_CONNECTOR_MANAGE.replace(
                  ':connectorType',
                  connectorType as string
                )}
              >
                Manage
              </Link>{' '}
              tab.
            </span>
          )}
        </small>
      );
    }

    if (warning) {
      const isCin7 = connectorType?.toLowerCase() === 'cin7';
      return (
        <small className="d-flex flex-column gap-1 text-warning">
          <span>
            <b>
              {isCin7
                ? 'Cin7 does take longer to sync than the other connectors. They do offer a Flexi API purchase which allows for faster syncs.'
                : 'This connector seems to be taking longer than expected.'}
            </b>
          </span>
          <span>
            {canSync
              ? isCin7
                ? 'You can refresh to check the latest status'
                : 'Please try refreshing it'
              : 'You will be able to retry in a few minutes'}
          </span>
        </small>
      );
    }
  };

  // Determine allocation/role gating for Trial Balance
  const isXero = (connectorType || '').toLowerCase() === 'xero';
  const isAdminRole = ['admin', 'superadmin', 'orgadmin'].some(r => currentRole.includes(r));
  const isAllocatedToUsers = !!clientConnector?.Properties?.find((_: any) => _.Name === 'portal.user' && _.Value?.trim());
  const canRunTrialBalance = isXero && (isAdminRole || isAllocatedToUsers);

  return (
    <>
      <div
        className={`tw-shadow tw-transition-all hover:tw-shadow-lg hover:tw-ring tw-ring-offset-1 tw-ring-blue-500 card card-sm ${
          ['DISCONNECTED', 'DELETED'].includes(clientConnector.Status ?? '')
            ? 'disabled'
            : ''
        }`}
        style={{ height: 260 }}
      >
        <div className="card-header align-items-start">
          <div style={{ maxWidth: '80%' }}>
            <div className="d-flex flex-column gap-3">
              <span className="text-uppercase fw-bold">
                {(clientConnector.Status === 'DELETED' && (
                  <span className="p-1 px-2 fs-11 text-danger bg-danger bg-opacity-10 rounded-1 d-inline">
                    deleted
                  </span>
                )) ||
                  (clientConnector.Status === 'DISCONNECTED' && (
                    <span className="p-1 px-2 fs-11 text-warning bg-warning bg-opacity-10 rounded-1 d-inline">
                      disconnected
                    </span>
                  )) ||
                  (clientConnector.Log?.Forbidden && (
                    <span className="p-1 px-2 fs-11 text-warning bg-warning bg-opacity-10 rounded-1 d-inline">
                      disconnected
                    </span>
                  )) ||
                  (!['DISCONNECTED', 'DELETED'].includes(clientConnector.Status ?? '') && (
                    <span className="p-1 px-2 fs-11 text-success bg-success bg-opacity-10 rounded-1 d-inline">
                      Connected
                    </span>
                  ))}
              </span>
              <span className="d-flex flex-column">
                {clientConnector.Name !== clientConnector.OriginalName && (
                  <small className="fw-bold darker-text fs-11">
                    {clientConnector.OriginalName}
                  </small>
                )}
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    display: 'inline-block',
                  }}
                >
                  {clientConnector.Name}
                </span>
                {renderStatusText()}
              </span>
            </div>
          </div>
          <StatusIcon log={clientConnector.Log} status={clientConnector.Status} />
        </div>

        <div className="card-body">
          {renderBody()}
          {renderProgressAnimation()}

          {!shouldHideRefreshDetail && !refreshing && (
            <>
              <br />
              <b>Refresh detail:</b>
              <div className="d-flex align-items-center">
                <small
                  className="text-truncate"
                  style={{
                    maxWidth: '90%',
                    display: 'inline-block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={rawDetail}
                >
                  {truncatedDetail}
                </small>

                {truncated && (
                  <span
                    className="ms-1 text-primary"
                    style={{ cursor: 'pointer', fontSize: '0.9em' }}
                    onClick={() => setShowModal(true)}
                  >
                    more
                  </span>
                )}

                {clientConnector.Log?.RefreshDetail && (
                  <button
                    className="ms-2 btn btn-outline-secondary btn-xs btn-round"
                    onClick={onCopyRefreshDetailToClipboard}
                    title="Copy full message"
                  >
                    {showCopied ? (
                      <span className="material-symbols-rounded">check</span>
                    ) : (
                      <span className="material-symbols-rounded">content_copy</span>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="card-footer gap-2 mt-auto">
          {!['DISCONNECTED', 'DELETED'].includes(clientConnector.Status ?? '') && (
            <div className="flex flex-row gap-2">
              <button
                className="btn btn-sm btn-primary justify-content-center"
                disabled={!canSync}
                onClick={(event) =>
                  onProcessClientConnectorClick(event, clientConnector, false)
                }
              >
                {clientConnector.Log?.Forbidden
                  ? 'Retry'
                  : currentRole.includes('superadmin')
                  ? 'Incremental'
                  : 'Refresh'}
              </button>
        {canRunTrialBalance && (
        <button
                  className="btn btn-sm btn-outline-primary"
                  title="Trial Balance"
          onClick={(event) => {
            const btn = event.currentTarget as HTMLButtonElement | null;
            if (btn) btn.blur();
            // Pass the event through (handler will snapshot references)
            onTrialBalanceClick(event, clientConnector);
          }}
      onMouseUp={(e) => (e.currentTarget as HTMLButtonElement).blur()}
      onMouseLeave={(e) => (e.currentTarget as HTMLButtonElement).blur()}
                >
                  Trial Balance
                </button>
              )}
              {!clientConnector.Log?.Forbidden &&
                currentRole.includes('superadmin') && (
                  <button
                    className="btn btn-sm btn-outline-primary justify-content-center ms-auto"
                    disabled={!canSync}
                    onClick={(event) =>
                      onProcessClientConnectorClick(event, clientConnector, true)
                    }
                  >
                    Full
                  </button>
                )}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div
          className="modal show d-block"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog" style={{ marginTop: '10vh' }}>
            <div className="modal-content">
              <div
                className="modal-header d-flex align-items-center justify-content-between"
                style={{ backgroundColor: '#0e7bbb' }}
              >
                <h5 className="modal-title mb-0" style={{ color: '#fff' }}>
                  Full Refresh Detail
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={onCopyRefreshDetailToClipboard}
                  >
                    {showCopied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '1rem' }}>{rawDetail}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Business2CloudConnectorManageCard;
